/* eslint-disable no-constant-condition */
import {
	InputField,
	InputDate,
	Select,
	SideSelect,
	TextArea,
	UploadFiles,
} from '@welbe/web-design-system';
import Analytics from 'helpers/analytics';
import React from 'react';
import { FiPaperclip } from 'react-icons/fi';
import { getFormattedDatesSchedules } from 'utils/formatterDatesScheduled';
import { PreviewEmailType } from 'Views/Campaigns/constants/previewEmailType';
import { PreviewEmailTypeEnum } from 'Views/Campaigns/enum/previewEmailType.enum';
import {
	acceptedFileTypes,
	hours,
	superscriptionItems,
} from '../NewCampaign.enums';
import {
	CalendarContainer,
	Container,
	DateContainer,
	InputContainer,
	Superscription,
	SuperscriptionContainer,
	UploadContainer,
} from './NewCampaignForm.styles';

interface INewCampaignForm {
	content: {
		campaignId: string;
		communicationTypeId: string;
		name: string;
		useCustomTemplate: boolean;
		fileName?: string;
		messageTitle: string;
		messageBody: string;
		messageFile: string;
		sendingDate: string;
		sendingTime: string;
	};
	loading: boolean;
	error: boolean;
	loadingUpload: boolean;
	datesScheduled: any[];
	activeType: string;
	campaign: any;
	onChangeValue: (value: any, field: string) => void;
}

export const NewCampaignForm = ({
	content,
	error,
	loading,
	loadingUpload,
	datesScheduled,
	activeType,
	campaign,
	onChangeValue,
}: INewCampaignForm): JSX.Element => {
	const getTimeValue = () => {
		const time = hours.find((hour) => hour.value === content.sendingTime);
		return time ?? {};
	};

	const getFilledCampaign = (type: string): boolean => {
		if (campaign[type].sendingDate) {
			return true;
		}

		return false;
	};

	const formattedDatesScheduled = getFormattedDatesSchedules(datesScheduled);

	return (
		<Container>
			<InputContainer>
				<InputField
					id="campaignName"
					error={error && !content.name}
					label="Nombre de la comunicación"
					onChange={(value) => onChangeValue(value, 'name')}
					value={content.name}
					disabled={loading}
				/>
				<InputField
					id="campaignTitle"
					error={error && !content.messageTitle}
					label="Título"
					onChange={(value) => onChangeValue(value, 'messageTitle')}
					value={content.messageTitle}
					disabled={loading}
				/>
				<Select
					id="campaignTemplate"
					label="Tipo de plantilla de la communication"
					onChange={(value) => {
						Analytics.track(
							'campaign',
							'change-create-compaign-preview-template',
							{
								template: {
									id: value.id,
									label: value.value,
								},
							},
						);
						onChangeValue(value.id, 'useCustomTemplate');
					}}
					initialSelected={{
						id: content.useCustomTemplate
							? PreviewEmailTypeEnum.CUSTOM
							: PreviewEmailTypeEnum.DEFAULT,
						value: content.useCustomTemplate
							? PreviewEmailType[PreviewEmailTypeEnum.CUSTOM]
							: PreviewEmailType[PreviewEmailTypeEnum.DEFAULT],
					}}
					options={[
						{
							id: PreviewEmailTypeEnum.DEFAULT,
							value: PreviewEmailType[PreviewEmailTypeEnum.DEFAULT],
						},
						{
							id: PreviewEmailTypeEnum.CUSTOM,
							value: PreviewEmailType[PreviewEmailTypeEnum.CUSTOM],
						},
					]}
				/>
				<TextArea
					id="campaignMessage"
					label="Mensaje"
					rows={4}
					handleChange={(value) => onChangeValue(value, 'messageBody')}
					value={content.messageBody}
					disabled={loading || !content.useCustomTemplate}
					placeholder={
						!content.useCustomTemplate ? 'Plantilla patrón elegida.' : 'Mensaje'
					}
				/>
				<UploadContainer>
					<UploadFiles
						id="campaignFile"
						width="300px"
						iconOnly
						disabled={loading}
						loading={loadingUpload}
						icon={<FiPaperclip style={{ transform: 'rotate(315deg)' }} />}
						acceptedFileTypes={acceptedFileTypes}
						fileName={
							content.fileName?.split('campaign/communications/').pop() || ''
						}
						onHandleFile={(file) => onChangeValue(file, 'messageFile')}
					/>
				</UploadContainer>
			</InputContainer>

			<DateContainer>
				<CalendarContainer>
					<InputDate
						id="campaignDate"
						error={error && !content.sendingDate}
						label="Fecha de envío"
						disabled={loading}
						onChange={(date) => onChangeValue(date, 'sendingDate')}
						minDate={
							datesScheduled[datesScheduled.length - 1]?.date
								? new Date(datesScheduled[datesScheduled.length - 1]?.date)
								: new Date()
						}
						value={content.sendingDate}
						scheduledDates={formattedDatesScheduled}
					/>
					<SideSelect
						id="campaignTime"
						error={error && !content.sendingTime}
						disabled={loading}
						label="Hora de envío"
						options={hours}
						onChange={(time) => onChangeValue(time?.value || '', 'sendingTime')}
						value={getTimeValue()}
					/>
				</CalendarContainer>
				<SuperscriptionContainer>
					<p>{superscriptionItems[activeType]}</p>
					{superscriptionItems.map((item: any) => {
						if (item.type === activeType || getFilledCampaign(item.type)) {
							return (
								<Superscription key={item.name} color={item.color}>
									{item.name}
								</Superscription>
							);
						}
						return null;
					})}
				</SuperscriptionContainer>
			</DateContainer>
		</Container>
	);
};
